import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useModalClose } from '../hooks/useModalClose';

function RecombineCheck({ orderId, orderItems, orderData, totalPrice, tableName, orderName, theme, user, onClose, onRecombineCompleted }) {
    const { t } = useLanguage();
    useModalClose(onClose);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [allOptions, setAllOptions] = useState([]);

    // Fetch all options for mapping ids to names
    useEffect(() => {
        fetch(`${BACKEND_URL}/api/debug/options`)
            .then(res => res.json())
            .then(data => setAllOptions(data))
            .catch(() => setAllOptions([]));
    }, []);

    // Helper to get option names from item.options (JSON string or array)
    const getOptionNames = (item, allOptions) => {
        if (!item.options) return null;
        let optionIds = [];
        if (typeof item.options === 'string') {
            try {
                optionIds = JSON.parse(item.options);
            } catch {
                return null;
            }
        } else if (Array.isArray(item.options)) {
            optionIds = item.options;
        }
        if (!Array.isArray(optionIds) || optionIds.length === 0) return null;
        if (!allOptions) return optionIds;
        // Filter out options with selected === 1
        return optionIds
            .map(id => allOptions.find(o => o.id === id))
            .filter(opt => opt && opt.selected !== 1)
            .map(opt => opt.name);
    };

    const handleRecombineCheck = async () => {
        if (!orderData?.parent_tab_id) {
            setError(t('thisOrderDoesNotHaveAParentToRecombineWith'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Move all items from this split check back to the parent order
            for (const item of orderItems) {
                const response = await fetch(`${BACKEND_URL}/api/order-products/${item.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        order_id: orderData.parent_tab_id
                    }),
                });

                if (!response.ok) {
                    throw new Error(t('failedToMoveItemToParentCheck', { itemId: item.id }));
                }
            }

            // After moving all items, delete the split check order (optional - it will be empty)
            // This helps clean up the database
            const deleteResponse = await fetch(`${BACKEND_URL}/api/orders/${orderId}`, {
                method: 'DELETE'
            });

            // Note: Even if delete fails, the recombine was successful since items were moved
            if (!deleteResponse.ok) {
                console.warn(t('failedToDeleteEmptySplitCheckOrderButItemsMovedSuccessfully'));
            }

            console.log(t('checkRecombinedSuccessfully'));
            onRecombineCompleted();
        } catch (err) {
            console.error(t('errorRecombiningCheck') + ':', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                backgroundColor: theme?.colors?.surface || '#fff',
                borderRadius: theme?.borderRadius || 8,
                padding: '2rem',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}>
                <h2 style={{
                    color: theme?.colors?.primary || '#1976d2',
                    marginTop: 0,
                    textAlign: 'center'
                }}>
                    {t('recombineSplitCheck')}
                </h2>

                {/* Split Check Summary */}
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: theme?.colors?.background || '#f5f5f5',
                    borderRadius: theme?.borderRadius || 6,
                    border: '1px solid #ddd'
                }}>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: theme?.colors?.text }}>{t('splitCheckDetails')}</h3>
                    <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary }}>
                        {tableName && <div>{t('table')}: <strong>{tableName}</strong></div>}
                        {orderName && <div>{t('splitCheckName')}: <strong>{orderName}</strong></div>}
                        <div>{t('itemsToMove')}: <strong>{orderItems.length}</strong></div>
                        <div>{t('totalAmount')}: <strong>${totalPrice.toFixed(2)}</strong></div>
                        <div>{t('parentOrderId')}: <strong>{orderData?.parent_tab_id}</strong></div>
                    </div>
                </div>

                {/* Items List */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ color: theme?.colors?.text, marginBottom: '0.5rem' }}>
                        {t('itemsToBeMovedBackToParentCheck')}:
                    </h4>
                    <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: theme?.borderRadius || 4,
                        padding: '0.5rem'
                    }}>
                        {orderItems.map(item => {
                            const itemPrice = Number(item.price || 0) * item.quantity;
                            const discount = Number(item.discount || 0);
                            const finalPrice = itemPrice - discount;

                            return (
                                <div
                                    key={item.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '0.5rem',
                                        margin: '0.25rem 0',
                                        backgroundColor: theme?.colors?.background || '#f5f5f5',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: '1px solid #ddd'
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 'bold', color: theme?.colors?.text }}>
                                            {item.product_name} x{item.quantity}
                                        </div>
                                        {getOptionNames(item, allOptions) && getOptionNames(item, allOptions).length > 0 && (
                                            <div style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary }}>
                                                {getOptionNames(item, allOptions).join(', ')}
                                            </div>
                                        )}
                                        {item.note && (
                                            <div style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary, fontStyle: 'italic' }}>
                                                {t('note')}: {item.note}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{
                                        fontWeight: 'bold',
                                        color: theme?.colors?.text,
                                        marginLeft: '1rem'
                                    }}>
                                        ${finalPrice.toFixed(2)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Warning Message */}
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    borderRadius: theme?.borderRadius || 6,
                    border: '1px solid #ffeaa7',
                    fontSize: '0.9rem'
                }}>
                    <strong>⚠️ {t('warning')}:</strong> {t('recombineWarningMessage')}
                </div>

                {/* Total Amount */}
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: '#ff9800',
                    color: '#fff',
                    borderRadius: theme?.borderRadius || 6,
                    textAlign: 'center'
                }}>
                    <h3 style={{ margin: '0', fontSize: '1.2rem' }}>
                        {t('totalBeingRecombined')}: ${totalPrice.toFixed(2)}
                    </h3>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '0.5rem',
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        borderRadius: theme?.borderRadius || 4,
                        fontSize: '0.9rem',
                        textAlign: 'center'
                    }}>
                        {error}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{
                            backgroundColor: theme?.colors?.secondary || '#666',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.75rem 1.5rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        onClick={handleRecombineCheck}
                        disabled={loading}
                        style={{
                            backgroundColor: loading ? '#ccc' : '#ff9800',
                            color: '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.75rem 1.5rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {loading ? t('recombining') : t('recombineCheck')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default RecombineCheck;